import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useSearchCardsQuery } from 'store/services/cards/search/cardsSearchApi';
import { useWindowSize } from 'hooks/useWindowSize';
import CardImageTooltip from './CardImageTooltip';
import { buildCardUrl } from 'utils/cardUrlUtils';
import './CardLink.css';

interface CardLinkProps {
  cardName: string;
  children?: React.ReactNode;
}

// Module-level cache for card data to avoid redundant API calls
const cardDataCache = new Map<string, { scryfallId: string; name: string; setCode?: string; collectorNumber?: string | null; imageUrl?: string; normalFallbacks?: string[] }>();

const SHOW_DELAY_MS = 50;
const HIDE_DELAY_MS = 300;
const MOBILE_BREAKPOINT = 768;

const CardLink = ({ cardName, children }: CardLinkProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const linkRef = useRef<HTMLAnchorElement>(null);

  const { isMobile } = useWindowSize(MOBILE_BREAKPOINT);

  // Check cache first before making API call
  const cachedData = cardDataCache.get(cardName.toLowerCase());

  // Only fetch when hovering and not in cache
  const shouldFetch = isHovered && !cachedData;

  const { data, isLoading, isError } = useSearchCardsQuery(
    { name: cardName, exact: true, limit: 1 },
    { skip: !shouldFetch }
  );

  // Get card data from API response or cache
  const card = data?.cards?.[0];
  const scryfallId = cachedData?.scryfallId || card?.scryfallID;
  const imageUrl = cachedData?.imageUrl || card?.imageUris?.normal;
  const normalFallbacks = cachedData?.normalFallbacks || (card as any)?.normal_fallbacks;

  // Cache the card data when loaded
  useEffect(() => {
    if (card && card.scryfallID && !cachedData) {
      cardDataCache.set(cardName.toLowerCase(), {
        scryfallId: card.scryfallID,
        name: card.name,
        setCode: card.setCode,
        collectorNumber: card.collectorNumber,
        imageUrl: card.imageUris?.normal,
        normalFallbacks: (card as any)?.normal_fallbacks,
      });
    }
  }, [card, cardName, cachedData]);

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      if (isMobile) return;

      // Clear any pending hide timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      setIsHovered(true);
      setTooltipPosition({ x: e.clientX, y: e.clientY });

      // Show tooltip after delay
      showTimeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
      }, SHOW_DELAY_MS);
    },
    [isMobile]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isMobile || !isHovered) return;

      // Update tooltip position as mouse moves
      setTooltipPosition({ x: e.clientX, y: e.clientY });
    },
    [isMobile, isHovered]
  );

  const handleMouseLeave = useCallback(() => {
    if (isMobile) return;

    // Clear show timeout
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }

    // Hide tooltip after delay
    hideTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
      setIsHovered(false);
    }, HIDE_DELAY_MS);
  }, [isMobile]);

  const handleTooltipMouseEnter = useCallback(() => {
    // Clear hide timeout when mouse enters tooltip
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const handleTooltipMouseLeave = useCallback(() => {
    // Start hide countdown when leaving tooltip
    hideTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
      setIsHovered(false);
    }, HIDE_DELAY_MS);
  }, []);

  // Build the best URL from cached/fetched data
  const cardUrl = useMemo(() => {
    const data = cachedData || (card ? { scryfallId: card.scryfallID, name: card.name, setCode: card.setCode, collectorNumber: card.collectorNumber } : null);
    if (data) {
      return buildCardUrl({ name: data.name, setCode: data.setCode, collectorNumber: data.collectorNumber, scryfallId: data.scryfallId });
    }
    return `/search/results?name=${encodeURIComponent(cardName)}&exact=true`;
  }, [cachedData, card, cardName]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      window.open(cardUrl, '_blank', 'noopener,noreferrer');
    },
    [cardUrl]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick(e as unknown as React.MouseEvent);
      }
    },
    [handleClick]
  );

  // Handle focus for keyboard accessibility
  const handleFocus = useCallback(() => {
    if (isMobile) return;

    setIsHovered(true);

    // Get link position for tooltip
    if (linkRef.current) {
      const rect = linkRef.current.getBoundingClientRect();
      setTooltipPosition({ x: rect.left, y: rect.bottom + 5 });
    }

    showTimeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, SHOW_DELAY_MS);
  }, [isMobile]);

  const handleBlur = useCallback(() => {
    if (isMobile) return;

    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
    }

    hideTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
      setIsHovered(false);
    }, HIDE_DELAY_MS);
  }, [isMobile]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  // Determine loading state
  const tooltipIsLoading = isLoading || (isHovered && !cachedData && !card && !isError);

  return (
    <>
      <a
        ref={linkRef}
        className="card-link"
        href={cardUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`View ${cardName} card details`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {children || cardName}
      </a>
      {showTooltip && !isMobile && (
        <CardImageTooltip
          scryfallId={scryfallId}
          imageUrl={imageUrl}
          normalFallbacks={normalFallbacks}
          cardName={cardName}
          position={tooltipPosition}
          isLoading={tooltipIsLoading}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        />
      )}
    </>
  );
};

export default CardLink;
